// How the invoice branch of the billing card writes money and dates (PRD 112
// Wave 2).
//
// MONEY arrives as integer cents and goes through `formatAmount`, the one
// display formatter for money: two decimals, grouped, a minus sign for a
// negative. Null is unpriced, which is not the same as free, so it reads "—".
//
// DATES are platform dates: calendar days in Eastern time (the api's
// PlatformBilling::ZONE), sent as YYYY-MM-DD. They are formatted from their
// parts, never through `new Date("YYYY-MM-DD")`, which is UTC midnight and
// shows the day before anywhere in the US. The one instant the web turns into
// a day (the billing alert's `suspends_on`) is read in Eastern time too, so a
// reader in California and one in New York see the same last day.

import { formatAmount } from '../../../../utils/money';
import { localISODate, parseLocalDate } from '../../../../utils/localDate';
import { formatBillingDate } from '../../../../pages/manageSubscriptionModel';

/** The zone platform billing dates are in (the api's Revenue::DAY_ZONE). */
export const PLATFORM_ZONE = 'America/New_York';

/** Integer cents as money: "$1,234.56". Null or absent is unpriced: "—". */
export function formatInvoiceCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '—';
  const whole = Math.round(cents);
  const abs = Math.abs(whole);
  // Built from the integer, so no float ever stands between cents and the string.
  const decimal = `${whole < 0 ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
  return formatAmount(decimal, false);
}

/** A platform date as "Nov 30, 2026"; null for nothing to show. */
export function formatInvoiceDate(value: string | null | undefined): string | null {
  return formatBillingDate(value, 'short');
}

/**
 * An invoice's period: "Nov 14 – 30, 2026", "Nov 1 – Dec 31, 2026", or one day
 * on its own. Falls back to the two dates when either is not a calendar date.
 */
export function formatInvoicePeriod(start: string, end: string): string {
  const from = parseLocalDate(start);
  const to = parseLocalDate(end);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return `${start} – ${end}`;
  if (start === end) return formatInvoiceDate(end) ?? end;

  const sameYear = from.getFullYear() === to.getFullYear();
  const sameMonth = sameYear && from.getMonth() === to.getMonth();
  const startText = from.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }),
  });
  const endText = sameMonth
    ? `${to.getDate()}, ${to.getFullYear()}`
    : to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startText} – ${endText}`;
}

/**
 * A platform date `days` later, as YYYY-MM-DD, worked out on its parts (never
 * through UTC). Null when it is not a calendar date.
 */
export function addPlatformDays(value: string, days: number): string | null {
  const date = parseLocalDate(value);
  if (Number.isNaN(date.getTime()) || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const later = new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${later.getFullYear()}-${pad(later.getMonth() + 1)}-${pad(later.getDate())}`;
}

/**
 * The platform day (Eastern) an instant falls on, as "Nov 7, 2026": a
 * request's expiry, say. Null when it is not an instant.
 */
export function formatInstantDay(instant: string | null | undefined): string | null {
  if (!instant) return null;
  const at = Date.parse(instant);
  if (Number.isNaN(at)) return null;
  return formatInvoiceDate(localISODate(new Date(at), PLATFORM_ZONE));
}

/**
 * The last day the books are writable, from the instant they turn read-only
 * (the alert's `suspends_on` for an invoice alert: the start of the first
 * read-only day, Eastern). Null when there is no such instant.
 */
export function lastWritableDay(suspendsOn: string | null | undefined): string | null {
  if (!suspendsOn) return null;
  const at = Date.parse(suspendsOn);
  if (Number.isNaN(at)) return null;
  return localISODate(new Date(at - 1), PLATFORM_ZONE);
}
